import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Pagination, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, MenuItem, IconButton, Divider,
  Alert,
} from '@mui/material';
import { Add, Delete, Print, ShoppingCart, Send } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useDispatch } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const statusColor = { draft: 'default', sent: 'info', accepted: 'success', rejected: 'error', converted: 'success', cancelled: 'default' };

function QuotePrint({ quote, tenant }) {
  if (!quote) return null;
  const items = Array.isArray(quote.items) ? quote.items : (typeof quote.items === 'string' ? JSON.parse(quote.items) : []);
  return (
    <Box sx={{ p: 4, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
      <style>{`@media print { @page { size: A4; margin: 20mm; } }`}</style>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{tenant?.name || 'Business Name'}</Typography>
          <Typography variant="body2" color="text.secondary">{tenant?.address}</Typography>
          <Typography variant="body2" color="text.secondary">{tenant?.phone}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" fontWeight={700} color="primary">QUOTATION</Typography>
          <Typography variant="body2">#{quote.quote_number}</Typography>
          <Typography variant="body2">Date: {new Date(quote.created_at).toLocaleDateString('en-PK')}</Typography>
          {quote.valid_until && <Typography variant="body2" color="error">Valid until: {new Date(quote.valid_until).toLocaleDateString('en-PK')}</Typography>}
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {quote.customer_name && (
        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={600}>To: {quote.customer_name}</Typography>
          {quote.customer_phone && <Typography variant="body2">{quote.customer_phone}</Typography>}
        </Box>
      )}
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell>#</TableCell>
            <TableCell>Item</TableCell>
            <TableCell align="center">Qty</TableCell>
            <TableCell align="right">Unit Price</TableCell>
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{item.product_name || item.name}</TableCell>
              <TableCell align="center">{item.quantity}</TableCell>
              <TableCell align="right">₨ {fmt(item.unit_price)}</TableCell>
              <TableCell align="right">₨ {fmt(item.unit_price * item.quantity)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ minWidth: 220 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography>Subtotal:</Typography><Typography>₨ {fmt(quote.subtotal)}</Typography>
          </Box>
          {+quote.discount_amount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, color: 'error.main' }}>
              <Typography>Discount:</Typography><Typography>- ₨ {fmt(quote.discount_amount)}</Typography>
            </Box>
          )}
          {+quote.tax_amount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography>Tax:</Typography><Typography>₨ {fmt(quote.tax_amount)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <Typography fontWeight={700}>Total:</Typography><Typography fontWeight={700} color="primary.main">₨ {fmt(quote.total_amount)}</Typography>
          </Box>
        </Box>
      </Box>
      {quote.notes && <Box sx={{ mt: 3, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}><Typography variant="body2"><strong>Notes:</strong> {quote.notes}</Typography></Box>}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ddd', textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">This is a quotation, not a tax invoice. Valid for {quote.valid_until ? `until ${new Date(quote.valid_until).toLocaleDateString('en-PK')}` : '30 days'}.</Typography>
      </Box>
    </Box>
  );
}

function ConvertDialog({ quote, open, onClose, onSuccess, dispatch }) {
  const [paidAmount, setPaidAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open && quote) setPaidAmount(String(Math.round(quote.total_amount))); }, [open, quote]);

  const convert = async () => {
    setLoading(true);
    try {
      await api.post(`/quotations/${quote.id}/convert`, { paid_amount: +paidAmount, payment_method: method });
      dispatch(showSnackbar({ message: 'Quotation converted to sale!', severity: 'success' }));
      onSuccess();
    } catch (e) { dispatch(showSnackbar({ message: e.response?.data?.message || 'Failed', severity: 'error' })); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Convert to Sale — {quote?.quote_number}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info">Total: ₨ {fmt(quote?.total_amount)}</Alert>
          <TextField label="Amount Paid ₨" type="number" value={paidAmount} onChange={e => setPaidAmount(e.target.value)} fullWidth />
          <TextField select label="Payment Method" value={method} onChange={e => setMethod(e.target.value)} fullWidth>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="card">Card</MenuItem>
            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
            <MenuItem value="credit">Credit</MenuItem>
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" onClick={convert} disabled={loading} startIcon={<ShoppingCart />}>
          {loading ? <CircularProgress size={20} /> : 'Convert to Sale'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function QuotationsPage() {
  const dispatch = useDispatch();
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [convertQuote, setConvertQuote] = useState(null);
  const [printQuote, setPrintQuote] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [items, setItems] = useState([{ product_id: '', product_name: '', quantity: 1, unit_price: 0 }]);
  const [formData, setFormData] = useState({ customer_id: '', discount_amount: 0, tax_amount: 0, notes: '', valid_until: '' });
  const printRef = useRef();
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/quotations', { params: { page, limit: 15 } });
      setQuotes(data.data); setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/customers?limit=100').then(r => setCustomers(r.data.data));
    api.get('/products?limit=200').then(r => setProducts(r.data.data));
    api.get('/settings/business').then(r => setTenant(r.data.data));
  }, []);

  const addItem = () => setItems(i => [...i, { product_id: '', product_name: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => setItems(i => i.filter((_, x) => x !== idx));
  const updateItem = (idx, field, val) => setItems(i => i.map((it, x) => x === idx ? { ...it, [field]: val } : it));
  const onProductChange = (idx, id) => {
    const p = products.find(p => p.id === +id);
    if (p) updateItem(idx, 'unit_price', p.sale_price);
    updateItem(idx, 'product_id', id);
    updateItem(idx, 'product_name', p?.name || '');
  };

  const subtotal = items.reduce((s, i) => s + (+i.unit_price * +i.quantity), 0);

  const onSubmit = async () => {
    try {
      await api.post('/quotations', { ...formData, items });
      dispatch(showSnackbar({ message: 'Quotation created', severity: 'success' }));
      setDialogOpen(false); load();
    } catch (e) { dispatch(showSnackbar({ message: 'Error', severity: 'error' })); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Quotations / Estimates</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setItems([{ product_id: '', product_name: '', quantity: 1, unit_price: 0 }]); setFormData({ customer_id: '', discount_amount: 0, tax_amount: 0, notes: '', valid_until: '' }); setDialogOpen(true); }}>
          New Quotation
        </Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quote #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Valid Until</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotes.map(q => (
                  <TableRow key={q.id} hover>
                    <TableCell><Typography fontWeight={700} color="primary">#{q.quote_number}</Typography></TableCell>
                    <TableCell>{q.customer_name || 'Walk-in'}</TableCell>
                    <TableCell align="right">₨ {fmt(q.total_amount)}</TableCell>
                    <TableCell>{q.valid_until ? new Date(q.valid_until).toLocaleDateString('en-PK') : '—'}</TableCell>
                    <TableCell><Chip label={q.status} size="small" color={statusColor[q.status] || 'default'} /></TableCell>
                    <TableCell><Typography variant="caption">{new Date(q.created_at).toLocaleDateString('en-PK')}</Typography></TableCell>
                    <TableCell>
                      <IconButton size="small" title="Print" onClick={async () => {
                        const { data } = await api.get(`/quotations/${q.id}`);
                        setPrintQuote(data.data);
                        setTimeout(handlePrint, 200);
                      }}><Print fontSize="small" /></IconButton>
                      {q.status !== 'converted' && q.status !== 'cancelled' && (
                        <Button size="small" color="success" startIcon={<ShoppingCart />} onClick={() => setConvertQuote(q)}>Convert</Button>
                      )}
                      {q.status === 'draft' && (
                        <IconButton size="small" color="error" onClick={async () => {
                          await api.delete(`/quotations/${q.id}`);
                          dispatch(showSnackbar({ message: 'Cancelled', severity: 'success' })); load();
                        }}><Delete fontSize="small" /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {quotes.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No quotations yet</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Quotation</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth select label="Customer (Optional)" value={formData.customer_id} onChange={e => setFormData(f => ({ ...f, customer_id: e.target.value }))}>
                <MenuItem value="">— Walk-in —</MenuItem>
                {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Valid Until" type="date" InputLabelProps={{ shrink: true }} value={formData.valid_until} onChange={e => setFormData(f => ({ ...f, valid_until: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Notes" value={formData.notes} onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} mb={1}>Items</Typography>
          {items.map((item, idx) => (
            <Grid container spacing={1} key={idx} sx={{ mb: 1, alignItems: 'flex-end' }}>
              <Grid item xs={12} md={5}>
                <TextField fullWidth size="small" select label="Product" value={item.product_id} onChange={e => onProductChange(idx, e.target.value)}>
                  {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4} md={2}>
                <TextField fullWidth size="small" label="Qty" type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} />
              </Grid>
              <Grid item xs={4} md={2.5}>
                <TextField fullWidth size="small" label="Unit Price ₨" type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} />
              </Grid>
              <Grid item xs={3} md={2}>
                <Typography variant="body2" fontWeight={600} sx={{ pt: 1 }}>₨ {fmt(+item.unit_price * +item.quantity)}</Typography>
              </Grid>
              <Grid item xs={1} md={0.5}>
                {items.length > 1 && <IconButton size="small" color="error" onClick={() => removeItem(idx)}><Delete fontSize="small" /></IconButton>}
              </Grid>
            </Grid>
          ))}
          <Button size="small" startIcon={<Add />} onClick={addItem}>Add Item</Button>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Discount ₨" type="number" value={formData.discount_amount} onChange={e => setFormData(f => ({ ...f, discount_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Tax ₨" type="number" value={formData.tax_amount} onChange={e => setFormData(f => ({ ...f, tax_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 1.5, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="body2">Total</Typography>
                <Typography variant="h6" fontWeight={700}>₨ {fmt(subtotal - +formData.discount_amount + +formData.tax_amount)}</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onSubmit} startIcon={<Send />}>Create Quotation</Button>
        </DialogActions>
      </Dialog>

      <ConvertDialog quote={convertQuote} open={!!convertQuote} onClose={() => setConvertQuote(null)} onSuccess={() => { setConvertQuote(null); load(); }} dispatch={dispatch} />

      {/* Hidden print area */}
      <Box sx={{ display: 'none' }}>
        <Box ref={printRef}><QuotePrint quote={printQuote} tenant={tenant} /></Box>
      </Box>
    </Box>
  );
}
